import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, GraduationCap, Briefcase } from "lucide-react";

const CTAS = [
  { to: "/get-started", label: "Get Started", cls: "btn-gold", id: "hero-get-started-button" },
  { to: "/book", label: "Book a Consultation", cls: "btn-outline-gold", id: "hero-book-consultation-button" },
  { to: "/careers", label: "Join the B2R Team", cls: "btn-glass", id: "hero-join-team-button" },
];

export const Hero = () => (
  <section className="relative min-h-[100svh] flex items-end overflow-hidden" data-testid="hero-section">
    <motion.img
      src="/images/hero.jpg" srcSet="/images/hero-m.jpg 900w, /images/hero.jpg 1600w" sizes="100vw" alt="" aria-hidden fetchpriority="high"
      className="absolute inset-0 w-full h-full object-cover"
      initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: "easeOut" }}
    />
    <div className="absolute inset-0 bg-black/65" />
    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
    <div className="absolute top-1/3 -left-40 w-[600px] h-[600px] rounded-full bg-[#5D2E8C]/40 blur-[140px]" />
    <div className="relative max-w-7xl mx-auto w-full px-6 md:px-12 pb-14 md:pb-28 pt-32 md:pt-40">
      <motion.p className="eyebrow mb-6 md:mb-8 !text-[0.65rem] md:!text-[0.72rem]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        Certified to File Taxes in All 50 States<span className="hidden sm:inline"> · Financial Services & Tax Consulting</span>
      </motion.p>
      <motion.h1
        className="text-[2.5rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-medium leading-[1.02] tracking-tight max-w-5xl"
        initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        data-testid="hero-headline"
      >
        Welcome to Baggs 2 Riches LLC — <span className="gold-text italic">Where Strategy Meets Opportunity.</span>
      </motion.h1>
      <motion.p className="mt-6 md:mt-8 max-w-xl text-base md:text-lg text-white/75 font-light leading-relaxed"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}>
        Financial education, strategic tax preparation and business development — crafted for people ready to turn their bag into riches.
      </motion.p>
      <div className="mt-9 md:mt-12 flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4">
        {CTAS.map((c, i) => (
          <motion.div key={c.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 + i * 0.15 }}>
            <Link to={c.to} className={`${c.cls} w-full sm:w-auto`} data-testid={c.id}>
              {c.label} {i === 0 && <ArrowRight className="w-4 h-4" />}
            </Link>
          </motion.div>
        ))}
      </div>
      <motion.div className="mt-7 flex flex-wrap gap-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} data-testid="hero-quick-links">
        <Link to="/academy" className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/40 backdrop-blur-md px-4 py-2.5 text-sm text-white/85 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="hero-classes-link">
          <GraduationCap className="w-4 h-4 text-[#D4AF37]" /> Classes Offered <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
        <Link to="/careers" className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/40 backdrop-blur-md px-4 py-2.5 text-sm text-white/85 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="hero-careers-link">
          <Briefcase className="w-4 h-4 text-[#D4AF37]" /> Careers & Opportunities <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
      </motion.div>
      <div className="mt-20 hidden md:flex items-center gap-3 text-xs tracking-[0.3em] uppercase text-white/40">
        <ArrowDown className="w-4 h-4 animate-bounce text-[#D4AF37]" /> Strategy Over Everything
      </div>
    </div>
  </section>
);

export const Marquee = () => {
  const words = ["Strategy Over Everything", "Tax Preparation", "Business Development", "Financial Freedom", "Simply Good People"];
  const row = [...words, ...words];
  return (
    <div className="border-y border-[#D4AF37]/25 bg-black py-6 overflow-hidden" data-testid="marquee">
      <div className="marquee-track flex whitespace-nowrap gap-14 w-max">
        {[...row, ...row].map((w, i) => (
          <span key={i} className="font-display text-2xl md:text-3xl italic text-white/80 flex items-center gap-14">
            {w} <span className="text-[#D4AF37] not-italic text-base">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
};
